import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Switch } from "@/components/ui/switch";
import { TutorialHint } from "@/components/tutorial-hint";
import { useTutorialMode } from "@/contexts/tutorial-mode";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Lock, Info, Upload, Sparkles } from "lucide-react";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { getAvatarSource, getAvatarInitials } from "@/lib/avatar";
import { useProfilePictureUpload, type UseProfilePictureUploadReturn } from "@/hooks/use-profile-picture-upload";

const profileSchema = z.object({
  title: z.string().max(100).optional().nullable(),
  location: z.string().max(100).optional().nullable(),
  bioShort: z.string().max(255).optional().nullable(),
  bioLong: z.string().optional().nullable(),
  contactEmail: z.string().email().optional().nullable().or(z.literal("")),
  contactPhone: z.string().max(50).optional().nullable(),
  socialLinks: z.object({
    website: z.string().optional(),
    instagram: z.string().optional(),
    tiktok: z.string().optional(),
    youtube: z.string().optional(),
    linkedin: z.string().optional(),
  }).optional(),
  hobbies: z.string().optional().nullable(),
  favoriteQuote: z.string().optional().nullable(),
  favoriteMovieShow: z.string().max(255).optional().nullable(),
  coffeeOrder: z.string().max(100).optional().nullable(),
  favoriteSnack: z.string().max(100).optional().nullable(),
  isPublished: z.boolean().default(false),
});

type ProfileFormData = z.infer<typeof profileSchema>;

interface MyProfileEditorProps {
  onClose: () => void;
  pickFile: UseProfilePictureUploadReturn["pickFile"];
  fileInputProps: UseProfilePictureUploadReturn["fileInputProps"];
  isUploading: UseProfilePictureUploadReturn["isUploading"];
}

interface ProfileResponse {
  profile: {
    id: string;
    userId: string;
    profilePicture: string | null;
    title: string | null;
    location: string | null;
    bioShort: string | null;
    bioLong: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
    socialLinks: string | null;
    hobbies: string | null;
    favoriteQuote: string | null;
    favoriteMovieShow: string | null;
    coffeeOrder: string | null;
    favoriteSnack: string | null;
    isPublished: boolean;
  } | null;
  settings: {
    allowTitleEdit: boolean;
    isWhitelisted: boolean;
  };
  userName?: string;
  profileImageUrl?: string | null;
}

export default function MyProfileEditor({ onClose, pickFile, fileInputProps, isUploading }: MyProfileEditorProps) {
  const { toast } = useToast();

  const { data, isLoading, refetch } = useQuery<ProfileResponse>({
    queryKey: ["/api/profiles/me"],
  });

  const parseSocialLinks = (str: string | null): Record<string, string> => {
    if (!str) return {};
    try {
      return JSON.parse(str);
    } catch {
      return {};
    }
  };

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      title: "",
      location: "",
      bioShort: "",
      bioLong: "",
      contactEmail: "",
      contactPhone: "",
      socialLinks: { website: "", instagram: "", tiktok: "", youtube: "", linkedin: "" },
      hobbies: "",
      favoriteQuote: "",
      favoriteMovieShow: "",
      coffeeOrder: "",
      favoriteSnack: "",
      isPublished: false,
    },
    values: data?.profile
      ? {
          title: data.profile.title || "",
          location: data.profile.location || "",
          bioShort: data.profile.bioShort || "",
          bioLong: data.profile.bioLong || "",
          contactEmail: data.profile.contactEmail || "",
          contactPhone: data.profile.contactPhone || "",
          socialLinks: parseSocialLinks(data.profile.socialLinks),
          hobbies: data.profile.hobbies || "",
          favoriteQuote: data.profile.favoriteQuote || "",
          favoriteMovieShow: data.profile.favoriteMovieShow || "",
          coffeeOrder: data.profile.coffeeOrder || "",
          favoriteSnack: data.profile.favoriteSnack || "",
          isPublished: data.profile.isPublished || false,
        }
      : undefined,
  });

  const updateMutation = useMutation({
    mutationFn: async (formData: ProfileFormData) => {
      const payload = {
        ...formData,
        socialLinks: JSON.stringify(formData.socialLinks || {}),
      };
      return await apiRequest("PATCH", "/api/profiles/me", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/profiles/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/profiles/directory"] });
      toast({
        title: "Profile updated",
        description: "Your profile has been saved successfully.",
      });
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update profile",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (formData: ProfileFormData) => {
    updateMutation.mutate(formData);
  };


  // Mirror the sidebar / shared helper resolution: prefer the company-managed
  // `profilePicture`, fall back to the auth provider photo, then initials.
  const currentPhotoUrl = getAvatarSource({
    profilePicture: data?.profile?.profilePicture,
    profileImageUrl: data?.profileImageUrl,
  }) || null;
  const hasCustomPhoto = !!data?.profile?.profilePicture;
  const userName = data?.userName || "User";
  const getInitials = (name: string) => getAvatarInitials(name);

  const canEditTitle = data?.settings?.allowTitleEdit || data?.settings?.isWhitelisted;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Profile Photo</h3>
          
          <div className="flex items-center gap-6">
            <div className="relative">
              <Avatar className="h-24 w-24 border-2 border-border">
                <AvatarImage src={currentPhotoUrl || undefined} alt={userName} />
                <AvatarFallback className="text-2xl bg-muted">
                  {getInitials(userName)}
                </AvatarFallback>
              </Avatar>
              {isUploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-full">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <input
                {...fileInputProps}
                data-testid="input-profile-picture"
              />
              <Button
                type="button"
                variant="outline"
                onClick={pickFile}
                disabled={isUploading}
                data-testid="button-upload-photo"
              >
                {isUploading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                {hasCustomPhoto ? "Change Photo" : "Upload Photo"}
              </Button>
              <p className="text-xs text-muted-foreground">
                JPEG, PNG, GIF or WebP. Max 5MB. Cropped to a square.
              </p>
            </div>
          </div>
        </div>

        <Separator />

        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Basic Info</h3>
          
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  Job Title
                  {!canEditTitle && <Lock className="h-3 w-3 text-muted-foreground" />}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value || ""}
                    placeholder="e.g., Senior Sales Representative"
                    disabled={!canEditTitle}
                    data-testid="input-profile-title"
                  />
                </FormControl>
                {!canEditTitle && (
                  <FormDescription className="flex items-center gap-1 text-amber-600">
                    <Info className="h-3 w-3" />
                    Title editing is currently locked by an admin
                  </FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="location"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Location</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value || ""}
                    placeholder="e.g., Austin, TX"
                    data-testid="input-profile-location"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="bioShort"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Short Bio (for cards)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value || ""}
                    placeholder="A brief tagline about yourself"
                    maxLength={255}
                    data-testid="input-profile-bio-short"
                  />
                </FormControl>
                <FormDescription>{(field.value || "").length}/255 characters</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="bioLong"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Full Bio</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    value={field.value || ""}
                    placeholder="Tell us more about yourself, your experience, and what you bring to the team..."
                    rows={4}
                    data-testid="textarea-profile-bio-long"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Separator />

        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Contact</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="contactEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact Email</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      value={field.value || ""}
                      placeholder="your@email.com"
                      data-testid="input-profile-contact-email"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contactPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact Phone</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value || ""}
                      placeholder="(555) 123-4567"
                      data-testid="input-profile-contact-phone"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <Separator />

        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Social Links</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="socialLinks.website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Website</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="https://yourwebsite.com" data-testid="input-profile-website" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="socialLinks.instagram"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Instagram</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="https://instagram.com/username" data-testid="input-profile-instagram" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="socialLinks.tiktok"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>TikTok</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="https://tiktok.com/@username" data-testid="input-profile-tiktok" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="socialLinks.youtube"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>YouTube</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="https://youtube.com/@channel" data-testid="input-profile-youtube" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="socialLinks.linkedin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>LinkedIn</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="https://linkedin.com/in/username" data-testid="input-profile-linkedin" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <Separator />

        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Fun Facts</h3>
          
          <FormField
            control={form.control}
            name="hobbies"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Hobbies & Interests</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    value={field.value || ""}
                    placeholder="What do you enjoy doing outside of work?"
                    rows={2}
                    data-testid="textarea-profile-hobbies"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="favoriteQuote"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Favorite Quote</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value || ""}
                    placeholder="A quote that inspires you"
                    data-testid="input-profile-quote"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="favoriteMovieShow"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Favorite Movie/Show</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value || ""}
                      placeholder="e.g., The Office"
                      data-testid="input-profile-movie"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="coffeeOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Coffee Order</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value || ""}
                      placeholder="e.g., Oat milk latte"
                      data-testid="input-profile-coffee"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="favoriteSnack"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Favorite Snack</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value || ""}
                    placeholder="e.g., Dark chocolate"
                    data-testid="input-profile-snack"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Separator />

        <HelpAndTutorialsSection onClose={onClose} />

        <Separator />

        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Visibility</h3>
          
          <FormField
            control={form.control}
            name="isPublished"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Publish Profile</FormLabel>
                  <FormDescription>
                    When published, your profile will be visible to all team members
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    data-testid="switch-profile-published"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose} data-testid="button-cancel-profile">
            Cancel
          </Button>
          <Button type="submit" disabled={updateMutation.isPending} data-testid="button-save-profile">
            {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Profile
          </Button>
        </div>
      </form>
    </Form>
  );
}

function HelpAndTutorialsSection({ onClose }: { onClose: () => void }) {
  const { enabled, toggle, isPending } = useTutorialMode();
  const { toast } = useToast();

  // Re-opening the welcome tour just clears this user's
  // onboardingCompletedAt — the same column the wizard reads on first
  // sign-in. The PATCH route already supports `completed: false`, and
  // App.tsx re-renders <OnboardingWizard /> as soon as the cached
  // profile shows null. We close the editor on success so the user
  // actually sees the wizard appear behind it.
  const replayWelcomeTour = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", "/api/profile/onboarding", {
        completed: false,
      });
      return res.json();
    },
    onSuccess: () => {
      // Both keys point at the same underlying profile row (one routes
      // via impersonation, one always returns the real user). Refresh
      // both so the App.tsx visibility gate flips immediately.
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/profile-real"] });
      toast({
        title: "Welcome tour reopened",
        description: "Closing your profile so you can take the tour again.",
      });
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: "Couldn't reopen the welcome tour",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Help &amp; Tutorials</h3>
      <div className="flex items-center justify-between rounded-lg border p-4">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <Label className="text-base">Show in-app help</Label>
            <TutorialHint id="profile.tutorialMode" />
          </div>
          <p className="text-sm text-muted-foreground">
            Display the small "?" hint icons throughout the app. Turn off once you know the ropes.
          </p>
        </div>
        <Switch
          checked={enabled}
          disabled={isPending}
          onCheckedChange={(next) => toggle(next)}
          data-testid="switch-tutorial-mode"
        />
      </div>
      <div className="flex items-center justify-between rounded-lg border p-4">
        <div className="space-y-0.5">
          <Label className="text-base">Welcome tour</Label>
          <p className="text-sm text-muted-foreground">
            Forgot where Leads vs. Clients live? Replay the friendly intro
            you saw on your first sign-in.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => replayWelcomeTour.mutate()}
          disabled={replayWelcomeTour.isPending}
          data-testid="button-replay-welcome-tour"
        >
          {replayWelcomeTour.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4 mr-2" />
          )}
          Show the welcome tour again
        </Button>
      </div>
    </div>
  );
}
